'use client'

import { useEffect, useRef } from 'react'
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext'
import { $getSelection, $isRangeSelection } from 'lexical'

const HIGHLIGHT_NAME = 'editor-persisted-selection'
const STYLE_ID = '__editor-highlight-style'

function ensureHighlightStyle() {
    if (typeof document === 'undefined') return
    if (document.getElementById(STYLE_ID)) return
    const style = document.createElement('style')
    style.id = STYLE_ID
    style.textContent = [
        `::highlight(${HIGHLIGHT_NAME}) { background-color: var(--primary); color: var(--primary-foreground); }`,
        `.rich-text-editor ::selection { background-color: var(--primary); color: var(--primary-foreground); }`,
    ].join('\n')
    document.head.appendChild(style)
}

export function clearHighlight() {
    if (typeof CSS !== 'undefined' && CSS.highlights) {
        CSS.highlights.delete(HIGHLIGHT_NAME)
    }
    if (typeof window !== 'undefined') {
        window.getSelection()?.removeAllRanges()
    }
}

function applyHighlight(range) {
    if (!range || typeof CSS === 'undefined' || !CSS.highlights) return
    try {
        ensureHighlightStyle()
        const highlight = new Highlight(range)
        CSS.highlights.set(HIGHLIGHT_NAME, highlight)
    } catch { /* browser doesn't support it */ }
}

export default function SelectionPlugin({ onSelectionChange }) {
    const [editor] = useLexicalComposerContext()
    const savedRangeRef = useRef(null)
    const savedTextRef = useRef(null)
    const mouseDownRef = useRef(false)

    useEffect(() => {
        ensureHighlightStyle()
    }, [])

    useEffect(() => {
        const rootElement = editor.getRootElement()
        if (!rootElement) return

        const handleMouseDown = () => {
            mouseDownRef.current = true
            clearHighlight()
            savedRangeRef.current = null
            savedTextRef.current = null
            onSelectionChange?.(null)
        }

        const handleMouseUp = () => {
            if (!mouseDownRef.current) return
            mouseDownRef.current = false

            const domSelection = window.getSelection()
            if (domSelection && domSelection.rangeCount > 0 && !domSelection.isCollapsed) {
                savedRangeRef.current = domSelection.getRangeAt(0).cloneRange()
            } else {
                savedRangeRef.current = null
            }

            editor.getEditorState().read(() => {
                const selection = $getSelection()
                if ($isRangeSelection(selection) && !selection.isCollapsed()) {
                    const text = selection.getTextContent()
                    savedTextRef.current = text
                    onSelectionChange?.(text)
                } else {
                    savedTextRef.current = null
                }
            })
        }

        const handleBlur = () => {
            if (savedRangeRef.current) {
                applyHighlight(savedRangeRef.current)
            }
        }

        const unregisterUpdate = editor.registerUpdateListener(({ dirtyElements, dirtyLeaves }) => {
            if (!savedTextRef.current) return
            if (dirtyElements.size === 0 && dirtyLeaves.size === 0) return

            const range = savedRangeRef.current
            let stillValid = false
            if (range) {
                try {
                    const currentText = range.toString()
                    stillValid = currentText === savedTextRef.current
                } catch {
                    stillValid = false
                }
            }

            if (!stillValid) {
                // Only drop the CSS Highlight — do NOT call clearHighlight()
                // here, because that also removes all DOM ranges. When the
                // user is actively editing (e.g. typed over the selected
                // text) the DOM selection is their live caret; nuking it
                // would leave the contenteditable with no insertion point
                // and the next keystroke would be dropped.
                if (typeof CSS !== 'undefined' && CSS.highlights) {
                    CSS.highlights.delete(HIGHLIGHT_NAME)
                }
                savedRangeRef.current = null
                savedTextRef.current = null
                onSelectionChange?.(null)
            }
        })

        rootElement.addEventListener('mousedown', handleMouseDown)
        rootElement.addEventListener('mouseup', handleMouseUp)
        rootElement.addEventListener('blur', handleBlur, true)

        return () => {
            rootElement.removeEventListener('mousedown', handleMouseDown)
            rootElement.removeEventListener('mouseup', handleMouseUp)
            rootElement.removeEventListener('blur', handleBlur, true)
            unregisterUpdate()
            clearHighlight()
        }
    }, [editor, onSelectionChange])

    return null
}
